import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FaPlus,
  FaEdit,
  FaSearch,
  FaArrowLeft,
  FaSpinner,
  FaUsers,
  FaTrash,
  FaExclamationTriangle,
  FaCalendarCheck,
} from "react-icons/fa";
import useApp from "../../hooks/useAppContext";
import { Link, useParams, useNavigate } from "react-router-dom";
import ModalB from "react-bootstrap/Modal";
import ModalInscritosAgenda from "../../components/agenda/ModalInscritosAgenda";
import useAuth from "../../hooks/useAuthContext";
import api from "../../services/axios";
import { toast } from "react-toastify";
import "./admPatrocinadoresList.css";
import "./adm.css";

const LABELS_TIPO = {
  geral: "Geral",
  conteudo_patrocinado: "Conteúdo patrocinado",
  conteudo_editorial: "Conteúdo editorial",
  esporte: "Esporte",
  reuniao_negocio: "Reunião de negócio",
  extraordinaria: "Extraordinária",
  acompanhante: "Acompanhante",
  kids: "Kids",
  m_m: "Match & Meet",
};

function horarioEvento(valor) {
  if (!valor || typeof valor !== "string" || valor.length < 16) return "—";
  return valor.substring(11, 16);
}

function qtdInscritos(evento) {
  return (evento?.participantes || []).length;
}

const TIPOS_SEM_ACOES_EM_MASSA = [
  "conteudo_editorial",
  "conteudo_patrocinado",
  "extraordinaria",
];

const AdmAgenda = () => {
  const navigate = useNavigate();
  const { getAgenda, getDiasEvento } = useApp();
  const { usuario } = useAuth();
  const { dia } = useParams();

  const [eventos, setEventos] = useState([]);
  const [diasEvento, setDiasEvento] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDias, setIsLoadingDias] = useState(true);
  const [error, setError] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [eventoModal, setEventoModal] = useState(null);
  const [participantesModal, setParticipantesModal] = useState([]);
  const [acaoModalLoading, setAcaoModalLoading] = useState(false);
  const [showConfirmExcluirTodos, setShowConfirmExcluirTodos] = useState(false);
  const [showConfirmCarregarGeral, setShowConfirmCarregarGeral] = useState(false);
  const [carregarGeralLoading, setCarregarGeralLoading] = useState(false);

  const isAdmin = usuario?.tipo === "administracao";
  const semAcoesEmMassa = TIPOS_SEM_ACOES_EM_MASSA.includes(eventoModal?.tipo);

  const carregarDias = useCallback(async () => {
    setIsLoadingDias(true);
    try {
      const dados = await getDiasEvento();
      const dias = dados?.dias || [];
      setDiasEvento(dias);
      return dias;
    } catch (err) {
      console.error("Erro ao carregar dias do evento:", err);
      setError("Erro ao carregar dias do evento");
      return [];
    } finally {
      setIsLoadingDias(false);
    }
  }, [getDiasEvento]);

  const carregarEventos = useCallback(async () => {
    if (!dia) return;

    setIsLoading(true);
    setError(null);
    try {
      const auxEventos = await getAgenda(dia);
      setEventos(auxEventos || []);
      return auxEventos || [];
    } catch (err) {
      console.error("Erro ao carregar agenda:", err);
      setError("Erro ao carregar lista da agenda");
      setEventos([]);
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [dia, getAgenda]);

  useEffect(() => {
    const iniciar = async () => {
      const dias = await carregarDias();
      if (!dia && dias.length > 0) {
        navigate(`/private/adm/agenda/${dias[0].dia}`, { replace: true });
      }
    };
    iniciar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carregarDias]);

  useEffect(() => {
    carregarEventos();
  }, [carregarEventos]);

  const eventosFiltrados = useMemo(() => {
    if (!filtro.trim()) return eventos;
    const termo = filtro.toLowerCase().trim();
    return eventos.filter((evento) => {
      const campos = [
        evento.nome,
        evento.tipo,
        LABELS_TIPO[evento.tipo],
        evento.local,
        horarioEvento(evento.dt_hora_inicio),
        horarioEvento(evento.dt_hora_fim),
      ];
      return campos.some((campo) =>
        String(campo || "")
          .toLowerCase()
          .includes(termo)
      );
    });
  }, [eventos, filtro]);

  const indicadores = useMemo(() => {
    return {
      total: eventos.length,
    };
  }, [eventos]);

  const diaAtivoLabel = useMemo(() => {
    const encontrado = diasEvento.find((item) => item.dia === dia);
    return encontrado?.label || dia || "—";
  }, [diasEvento, dia]);

  const sincronizarModalComEventos = useCallback((listaEventos, idAgenda) => {
    const atualizado = (listaEventos || []).find((e) => e.id === idAgenda);
    if (!atualizado) {
      setShowModal(false);
      setEventoModal(null);
      setParticipantesModal([]);
      return;
    }
    setEventoModal(atualizado);
    setParticipantesModal(atualizado.participantes || []);
  }, []);

  const handleModalInscritos = (item) => {
    setEventoModal(item);
    setParticipantesModal(item.participantes || []);
    setShowModal(true);
  };

  const fecharModal = () => {
    setShowModal(false);
    setEventoModal(null);
    setParticipantesModal([]);
    setAcaoModalLoading(false);
    setShowConfirmExcluirTodos(false);
  };

  const handleAdicionarTodos = async () => {
    if (!eventoModal?.id || acaoModalLoading) return;

    const confirmar = window.confirm(
      "Adicionar esta agenda a todos os usuários não deletados?\nQuem já estiver inscrito será ignorado."
    );
    if (!confirmar) return;

    setAcaoModalLoading(true);
    try {
      const resp = await api.post("/agenda-participantes-todos", {
        id_agenda: eventoModal.id,
      });
      const dados = resp.data?.dados || {};
      toast.success(
        `Adicionados: ${dados.adicionados ?? 0}. Já inscritos: ${
          dados.ja_inscritos ?? 0
        }.`
      );
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao adicionar agenda aos usuários."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  const handleRemoverParticipante = async (participante) => {
    if (!eventoModal?.id || !participante?.usuario?.id || acaoModalLoading) {
      return;
    }

    const nome = participante.usuario?.nome || "este participante";
    const confirmar = window.confirm(
      `Remover ${nome} desta agenda?\nA agenda será mantida.`
    );
    if (!confirmar) return;

    setAcaoModalLoading(true);
    try {
      await api.post("/agenda-participante-remover", {
        id_agenda: eventoModal.id,
        id_usuario: participante.usuario.id,
      });
      toast.success("Participante removido.");
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao remover participante."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  const abrirConfirmExcluirTodos = () => {
    if (!eventoModal?.id || acaoModalLoading || participantesModal.length === 0) {
      return;
    }
    setShowConfirmExcluirTodos(true);
  };

  const confirmarRemoverTodos = async () => {
    if (!eventoModal?.id || acaoModalLoading) return;

    setAcaoModalLoading(true);
    try {
      const resp = await api.post("/agenda-participantes-remover-todos", {
        id_agenda: eventoModal.id,
      });
      toast.success(
        `Removidos: ${resp.data?.dados?.removidos ?? 0} participante(s).`
      );
      setShowConfirmExcluirTodos(false);
      const lista = await carregarEventos();
      sincronizarModalComEventos(lista, eventoModal.id);
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem || "Erro ao remover participantes."
      );
    } finally {
      setAcaoModalLoading(false);
    }
  };

  const confirmarCarregarAgendaGeral = async () => {
    if (carregarGeralLoading) return;

    setCarregarGeralLoading(true);
    try {
      const resp = await api.post("/agenda-geral-carregar-todos");
      const dados = resp.data?.dados || {};
      toast.success(
        `Agenda geral carregada. Adicionados: ${
          dados.adicionados ?? 0
        }. Já inscritos: ${dados.ja_inscritos ?? 0}. Agendas: ${
          dados.total_agendas_geral ?? 0
        }.`
      );
      setShowConfirmCarregarGeral(false);
      await carregarEventos();
    } catch (err) {
      toast.error(
        err?.response?.data?.mensagem ||
          "Erro ao carregar agenda geral para os usuários."
      );
    } finally {
      setCarregarGeralLoading(false);
    }
  };

  return (
    <div className="container-adm-main">
      <ModalB
        show={showConfirmExcluirTodos}
        onHide={() => !acaoModalLoading && setShowConfirmExcluirTodos(false)}
        centered
        size="sm"
        className="agenda-confirm-excluir-modal"
        backdrop={acaoModalLoading ? "static" : true}
      >
        <ModalB.Header closeButton={!acaoModalLoading}>
          <ModalB.Title className="agenda-confirm-excluir-title">
            <FaExclamationTriangle
              size={18}
              className="agenda-confirm-excluir-icon"
              aria-hidden
            />
            Confirmar exclusão
          </ModalB.Title>
        </ModalB.Header>
        <ModalB.Body>
          <p className="mb-2">
            Remover <strong>todos</strong> os participantes inscritos da agenda{" "}
            <strong>{eventoModal?.nome || "selecionada"}</strong>?
          </p>
          <p className="mb-0 text-muted">
            Serão removidos{" "}
            <strong>{participantesModal.length}</strong> participante(s)
            inscrito(s). A agenda será mantida.
          </p>
        </ModalB.Body>
        <ModalB.Footer className="agenda-confirm-excluir-footer">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setShowConfirmExcluirTodos(false)}
            disabled={acaoModalLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn agenda-confirm-excluir-btn"
            onClick={confirmarRemoverTodos}
            disabled={acaoModalLoading}
          >
            {acaoModalLoading ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaTrash size={14} aria-hidden />
            )}
            {acaoModalLoading ? "Excluindo…" : "Excluir todos"}
          </button>
        </ModalB.Footer>
      </ModalB>

      <ModalB
        show={showConfirmCarregarGeral}
        onHide={() =>
          !carregarGeralLoading && setShowConfirmCarregarGeral(false)
        }
        centered
        size="sm"
        className="agenda-confirm-excluir-modal"
        backdrop={carregarGeralLoading ? "static" : true}
      >
        <ModalB.Header closeButton={!carregarGeralLoading}>
          <ModalB.Title className="agenda-confirm-excluir-title">
            <FaCalendarCheck
              size={18}
              className="agenda-confirm-excluir-icon"
              aria-hidden
            />
            Carregar agenda geral
          </ModalB.Title>
        </ModalB.Header>
        <ModalB.Body>
          <p className="mb-2">
            Esta ação irá carregar a <strong>agenda geral</strong> para{" "}
            <strong>todos os usuários ativos</strong> do sistema.
          </p>
          <p className="mb-0 text-muted">
            Usuários que já possuem cada item da agenda geral não receberão
            duplicidade. Deseja continuar?
          </p>
        </ModalB.Body>
        <ModalB.Footer className="agenda-confirm-excluir-footer">
          <button
            type="button"
            className="apl-btn apl-btn--ghost"
            onClick={() => setShowConfirmCarregarGeral(false)}
            disabled={carregarGeralLoading}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="apl-btn apl-btn--primary"
            onClick={confirmarCarregarAgendaGeral}
            disabled={carregarGeralLoading}
          >
            {carregarGeralLoading ? (
              <FaSpinner className="fa-spin" size={14} aria-hidden />
            ) : (
              <FaCalendarCheck size={14} aria-hidden />
            )}
            {carregarGeralLoading ? "Carregando…" : "Confirmar"}
          </button>
        </ModalB.Footer>
      </ModalB>

      <ModalInscritosAgenda
        show={showModal}
        onClose={fecharModal}
        titulo={`Inscritos — ${eventoModal?.nome || "Agenda"}`}
        participantes={participantesModal}
        imprimirTo={
          eventoModal?.id
            ? `/private/adm/conteudo/imprimir/${eventoModal.id}`
            : undefined
        }
        isAdmin={isAdmin}
        loading={acaoModalLoading}
        onRemoverParticipante={handleRemoverParticipante}
        onAdicionarTodos={semAcoesEmMassa ? undefined : handleAdicionarTodos}
        onExcluirTodos={semAcoesEmMassa ? undefined : abrirConfirmExcluirTodos}
        emptyHintSemInscritos={
          semAcoesEmMassa
            ? "Ainda não há participantes inscritos neste conteúdo."
            : 'Use “Adicionar a todos” para incluir os convidados.'
        }
      />

      <div className="apl-page apl-page--padded">
        <header className="apl-header" aria-label="Cabeçalho da listagem">
          <div className="apl-header-text">
            <h1>Agenda</h1>
            <p>
              Consulte os conteúdos por dia do evento, veja inscritos e edite
              os itens da programação.
            </p>
          </div>

          <div className="apl-toolbar apl-toolbar--inline">
            <div className="apl-search">
              <span className="apl-search-icon" aria-hidden>
                <FaSearch size={14} />
              </span>
              <input
                type="search"
                className="apl-search-input"
                placeholder="Buscar por horário, evento ou tipo…"
                value={filtro}
                onChange={(e) => setFiltro(e.target.value)}
                disabled={isLoading || isLoadingDias}
                aria-label="Buscar evento na agenda"
              />
            </div>

            <div className="apl-toolbar-actions">
              {isAdmin && (
                <>
                  <button
                    type="button"
                    className="apl-btn apl-btn--secondary"
                    onClick={() => setShowConfirmCarregarGeral(true)}
                    disabled={carregarGeralLoading || isLoading || isLoadingDias}
                    title="Carregar agenda geral para todos os usuários ativos"
                  >
                    {carregarGeralLoading ? (
                      <FaSpinner className="fa-spin" size={14} aria-hidden />
                    ) : (
                      <FaCalendarCheck size={14} aria-hidden />
                    )}
                    Carregar agenda geral
                  </button>

                  <Link
                    to="/private/adm/agenda-novo"
                    className="apl-btn apl-btn--primary"
                  >
                    <FaPlus size={14} aria-hidden />
                    Nova agenda
                  </Link>
                </>
              )}

              <button
                type="button"
                className="apl-btn apl-btn--ghost"
                onClick={() => navigate(-1)}
                title="Voltar"
              >
                <FaArrowLeft size={14} aria-hidden />
                Voltar
              </button>
            </div>
          </div>
        </header>

        <div className="body-tela-adm" style={{ paddingBottom: 0 }}>
          {error ? (
            <div className="apl-alert" role="alert">
              {error}
            </div>
          ) : null}

          {!isLoadingDias && diasEvento.length > 0 ? (
            <nav className="apl-day-tabs" aria-label="Dias do evento">
              {diasEvento.map((item) => (
                <Link
                  key={item.data}
                  to={`/private/adm/agenda/${item.dia}`}
                  className={`apl-day-tab${
                    dia === item.dia ? " is-active" : ""
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          ) : null}

          {!isLoading && !isLoadingDias && eventos.length > 0 ? (
            <section
              className="apl-metrics"
              aria-label="Indicadores da agenda"
            >
              <div className="apl-metric apl-metric--total">
                <span className="apl-metric-value">{indicadores.total}</span>
                <span className="apl-metric-label">
                  {indicadores.total === 1 ? "Evento" : "Eventos no dia"}
                </span>
              </div>
              <div className="apl-metric">
                <span className="apl-metric-value">{diaAtivoLabel}</span>
                <span className="apl-metric-label">Dia selecionado</span>
              </div>
            </section>
          ) : null}

          {isLoading || isLoadingDias ? (
            <div className="apl-card">
              <div className="apl-loading">
                <FaSpinner className="fa-spin" size={28} aria-hidden />
                <span>
                  {isLoadingDias
                    ? "Carregando dias do evento…"
                    : "Carregando agenda…"}
                </span>
              </div>
            </div>
          ) : (
            <section className="apl-card" aria-label="Tabela da agenda">
              <div className="apl-table-wrap">
                {eventosFiltrados.length === 0 ? (
                  <div className="apl-empty">
                    <p className="apl-empty-title">
                      {eventos.length === 0
                        ? "Nenhum evento cadastrado para essa data"
                        : "Nenhum resultado"}
                    </p>
                    <p style={{ margin: 0 }}>
                      {eventos.length === 0
                        ? "Cadastre um evento ou selecione outro dia."
                        : "Ajuste a busca para ver eventos."}
                    </p>
                  </div>
                ) : (
                  <table className="apl-table">
                    <thead>
                      <tr>
                        <th scope="col">Horário</th>
                        <th scope="col">Evento</th>
                        <th scope="col">Tipo</th>
                        <th scope="col">Inscritos</th>
                        <th scope="col" style={{ textAlign: "right" }}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventosFiltrados.map((evento) => {
                        const dtInicio = horarioEvento(evento.dt_hora_inicio);
                        const dtFim = horarioEvento(evento.dt_hora_fim);
                        const inscritos = qtdInscritos(evento);

                        return (
                          <tr key={evento.id}>
                            <td>
                              <span className="apl-stat">
                                {dtInicio} – {dtFim}
                              </span>
                            </td>
                            <td className="apl-cell-name">
                              {evento.nome || "—"}
                            </td>
                            <td>
                              {evento.tipo ? (
                                <span className="apl-tag-type">
                                  {LABELS_TIPO[evento.tipo] || evento.tipo}
                                </span>
                              ) : (
                                <span className="apl-stat-muted">—</span>
                              )}
                            </td>
                            <td>
                              <span className="apl-stat">{inscritos}</span>
                            </td>
                            <td>
                              <div className="apl-actions">
                                {isAdmin && (
                                  <Link
                                    to={`/private/adm/agenda-editar/${evento.id}`}
                                  >
                                    <FaEdit size={12} aria-hidden />
                                    Editar
                                  </Link>
                                )}
                                <button
                                  type="button"
                                  className="apl-action-btn"
                                  onClick={() => handleModalInscritos(evento)}
                                >
                                  <FaUsers size={12} aria-hidden />
                                  Inscritos
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {eventosFiltrados.length > 0 ? (
                <footer className="apl-footer">
                  <span>
                    Exibindo{" "}
                    <strong>{eventosFiltrados.length}</strong>{" "}
                    {eventosFiltrados.length === 1 ? "evento" : "eventos"}
                    {filtro ? (
                      <>
                        {" "}
                        <span className="apl-stat-muted">
                          (de {eventos.length} no dia)
                        </span>
                      </>
                    ) : null}
                  </span>
                  <span>Dia {diaAtivoLabel}</span>
                </footer>
              ) : null}
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdmAgenda;
